import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { ChevronDown, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * StepGateNextbar
 *
 * Reusable collapsible gate checklist displayed above the workflow footer.
 * Shows live completion status (✓ complete / ✕ incomplete) as fields are edited.
 * Clicking any requirement scrolls directly to its input and pulses an outline.
 */
const StepGateNextbar = forwardRef(({
    title = 'Ready to move on',
    completeness = { isComplete: false, items: [] },
    className = '',
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const { isComplete, items = [] } = completeness;

    const metCount = items.filter((i) => i.isMet).length;
    const totalCount = items.length;

    const scrollToElement = (id) => {
        if (!id) return false;
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
                if (typeof el.focus === 'function') {
                    el.focus({ preventScroll: true });
                }
                el.classList.add('epm-highlight-pulse');
                setTimeout(() => el.classList.remove('epm-highlight-pulse'), 1900);
            }, 250);
            return true;
        }
        return false;
    };

    // Expose method to parent (e.g. TestWorkflow handleNext)
    useImperativeHandle(ref, () => ({
        openAndScrollToFirstError: () => {
            setIsOpen(true);
            const firstUnmet = items.find((i) => !i.isMet);
            if (firstUnmet?.targetElementId) {
                scrollToElement(firstUnmet.targetElementId);
                return firstUnmet;
            }
            return null;
        },
        toggleOpen: () => setIsOpen((prev) => !prev),
        isOpen,
    }));

    return (
        <aside
            aria-label="Workflow step completeness checklist"
            className={`flex items-center gap-4 mt-8 pt-5 border-t border-border relative ${className}`}
        >
            <div className="epm-plan__collapsible flat flex-1 m-0 p-0 border-0 bg-transparent">
                <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    className={`epm-plan__collapsible-header ${isOpen ? 'is-open' : ''} py-2 px-0 cursor-pointer flex items-center gap-2 select-none w-full text-left bg-transparent border-0`}
                >
                    <ChevronDown size={16} className={`transition-transform duration-200 text-primary ${isOpen ? 'rotate-180' : ''}`} />
                    <span className="epm-plan__collapsible-title text-[13px] font-semibold text-stride-heading flex items-center gap-1.5">
                        {isComplete ? (
                            <CheckCircle2 size={15} className="text-emerald-600 inline flex-none" />
                        ) : (
                            <AlertCircle size={15} className="text-amber-500 inline flex-none" />
                        )}
                        {title}
                    </span>
                    <span className="epm-plan__collapsible-subtitle text-[12.5px] text-stride-muted ml-2 font-normal">
                        {isComplete ? (
                            <span className="text-emerald-700 font-medium">
                                All {totalCount} requirements satisfied
                            </span>
                        ) : (
                            <span>
                                {metCount} of {totalCount} completed · <strong className="text-rose-600 font-semibold">{totalCount - metCount} required</strong>
                            </span>
                        )}
                    </span>
                </button>

                {isOpen && (
                    <div className="epm-plan__collapsible-body pl-6 pr-0 pb-2 pt-2">
                        <ul className="m-0 p-0 list-none space-y-1.5 text-[12.5px]">
                            {items.map((item) => (
                                <li
                                    key={item.id}
                                    onClick={() => scrollToElement(item.targetElementId)}
                                    className={`py-1 px-2.5 rounded-md flex items-center justify-between gap-3 cursor-pointer transition-colors ${
                                        item.isMet
                                            ? 'text-emerald-800 bg-emerald-50/50 hover:bg-emerald-50'
                                            : 'text-rose-700 bg-rose-50/70 hover:bg-rose-100/70 font-medium'
                                    }`}
                                    title={item.targetElementId ? 'Click to jump to this field' : undefined}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className={`w-4 font-bold ${item.isMet ? 'text-emerald-600' : 'text-rose-600'}`}>
                                            {item.isMet ? '✓' : '✕'}
                                        </span>
                                        <span className="truncate">{item.label}</span>
                                    </div>
                                    {item.helpText && (
                                        <span className="text-[11px] opacity-75 font-normal flex-none">
                                            {item.helpText}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </aside>
    );
});

StepGateNextbar.displayName = 'StepGateNextbar';

export default StepGateNextbar;

